import Component from "@base/Component.js";
import { foldTag, cleanTag, splitTags, canonicalTag, matchTag, uniqueTags } from "@/services/tags.js";
import t from "@/services/translationService.js";

/**
 * Chip-and-typeahead tag field: one control that looks tags up and creates them.
 *
 * It replaces the editor's old comma-separated text box, which had no idea what
 * tags already existed — so every gallery re-typed the vocabulary from memory
 * and "Landschaft"/"landschaft"/"Landscape" all ended up in the filter list as
 * separate entries. Here, typing filters the known tags (case- and
 * accent-insensitively, `foldTag`), and committing a value that folds onto an
 * existing tag adopts *that* tag's spelling rather than adding a near-duplicate.
 * A genuinely new tag is still one keystroke away — the last suggestion is an
 * explicit "Create «…»" row, so creating is a deliberate act rather than the
 * accidental default it used to be.
 *
 * Suggestions carry a usage count for the same reason the public filter does:
 * "used in 7 galleries" is the difference between the tag you meant and the
 * typo of it someone made once.
 *
 * The list is a listbox with a keyboard cursor (↑/↓/Enter/Escape); Backspace on
 * an empty field peels off the last chip, as chip inputs generally do.
 *
 * Chips carry *keys* — what Gallery.tags stores — but show the display name for
 * the editing language, and the lookup matches either language, so typing
 * "Landschaft" adds the tag whose key is "Landscape". A tag invented here is
 * its own key; the backend gives it a vocabulary row named after itself in both
 * languages (tagService.ensure), leaving only the translation to fill in from
 * Cockpit → Tag Manager.
 *
 * props: {
 *   tags: string[],                                  // current tag keys
 *   vocabulary: [{ key, label, count, folded }],     // see tagManager.vocabulary
 *   onChange(keys): void,
 * }
 */
export default class TagInput extends Component {
  constructor(props) {
    super(props);
    this.state.tags = uniqueTags(props.tags || []);
    this.state.query = "";
    this.state.open = false;
    this.highlight = -1; // keyboard cursor into the suggestion list (transient)
  }

  /** What to show for a key: its name in the editing language, or the key. */
  #labelOf(key) {
    return (this.props.vocabulary || []).find((entry) => entry.key === key)?.label || key;
  }

  /** Tags not already on this gallery, filtered by the query, best match first. */
  #suggestions() {
    const q = foldTag(this.state.query.trim());
    const chosen = new Set(this.state.tags.map(foldTag));
    return (this.props.vocabulary || [])
      .filter(({ key }) => !chosen.has(foldTag(key)))
      .filter(({ folded }) => !q || folded.some((f) => f.includes(q)))
      .sort((a, b) => {
        // Rank on whichever spelling actually matched — the German name is as
        // good a handle as the key when that is what was typed.
        const aPrefix = q && a.folded.some((f) => f.startsWith(q));
        const bPrefix = q && b.folded.some((f) => f.startsWith(q));
        if (aPrefix !== bPrefix) return aPrefix ? -1 : 1;
        if (a.count !== b.count) return b.count - a.count;
        return a.label.localeCompare(b.label);
      })
      .slice(0, 50);
  }

  /**
   * Whether the query would create a new tag: only when it is non-empty and no
   * existing tag — vocabulary or already-chosen chip — folds onto it.
   */
  #creatable() {
    const clean = cleanTag(this.state.query);
    if (!clean) return "";
    if (matchTag(clean, this.props.vocabulary || [])) return "";
    const key = foldTag(clean);
    return this.state.tags.some((tag) => foldTag(tag) === key) ? "" : clean;
  }

  /** Suggestion rows, plus the "Create «…»" row when the query is new. */
  #optionMarkup() {
    const items = this.#suggestions();
    const creatable = this.#creatable();
    const rows = items.map(
      ({ key, label, count }, i) =>
        `<li><button type="button" class="tag-search__option ${i === this.highlight ? "tag-search__option--active" : ""}" role="option" aria-selected="${i === this.highlight}" data-tag="${Component.escape(key)}">
           <span class="tag-search__option-label">${Component.escape(label)}</span>
           <span class="tag-search__option-count">${count}</span>
         </button></li>`
    );

    if (creatable) {
      const i = items.length;
      rows.push(
        `<li><button type="button" class="tag-search__option tag-search__option--create ${i === this.highlight ? "tag-search__option--active" : ""}" role="option" aria-selected="${i === this.highlight}" data-create="${Component.escape(creatable)}">
           <span class="tag-search__option-label">+ ${Component.escape(t.t("cockpit.tags.field.create").replace("{tag}", creatable))}</span>
         </button></li>`
      );
    }

    if (!rows.length) {
      // Nothing to offer has three causes worth telling apart: an empty
      // vocabulary, a query that only matches tags already on this gallery,
      // and — impossible here, since anything else is creatable — no match.
      const query = cleanTag(this.state.query);
      const already =
        query &&
        this.state.tags.some(
          (key) => foldTag(key) === foldTag(query) || foldTag(this.#labelOf(key)) === foldTag(query)
        );
      const message = already
        ? t.t("cockpit.tags.field.already")
        : query
        ? t.t("cockpit.tags.field.noMatch")
        : t.t("cockpit.tags.field.empty");
      return `<li class="tag-search__empty">${Component.escape(message)}</li>`;
    }
    return rows.join("");
  }

  /** The option at the keyboard cursor, or the best one to commit on Enter. */
  #optionAt(index) {
    const items = this.#suggestions();
    const creatable = this.#creatable();
    if (index >= 0 && index < items.length) return items[index].key;
    if (creatable && index === items.length) return creatable;
    return null;
  }

  #optionCount() {
    return this.#suggestions().length + (this.#creatable() ? 1 : 0);
  }

  render() {
    const chips = this.state.tags
      .map((key) => {
        const label = this.#labelOf(key);
        return `<button type="button" class="tag-pill" data-remove="${Component.escape(key)}" aria-label="${Component.escape(t.t("cockpit.tags.field.remove").replace("{tag}", label))}">
             <span>${Component.escape(label)}</span><span class="tag-pill__x" aria-hidden="true">×</span>
           </button>`;
      })
      .join("");

    return `
      <div class="tag-input">
        <div class="tag-input__chips">${chips}</div>
        <div class="tag-search tag-input__search">
          <input type="text" class="tag-search__input" role="combobox" aria-expanded="${this.state.open}"
                 aria-controls="tag-input-suggestions" aria-autocomplete="list" autocomplete="off"
                 placeholder="${Component.escape(t.t("cockpit.tags.field.placeholder"))}" value="${Component.escape(this.state.query)}" />
          <ul class="tag-search__suggestions" id="tag-input-suggestions" role="listbox" ${this.state.open ? "" : "hidden"}>${
            this.state.open ? this.#optionMarkup() : ""
          }</ul>
        </div>
      </div>`;
  }

  onMounted() {
    const input = this.$(".tag-search__input");
    const list = this.$(".tag-search__suggestions");

    if (this.refocus) {
      input.focus();
      this.refocus = false;
    }

    // Typing only rebuilds the suggestion list, never the whole component —
    // re-rendering would replace the input mid-keystroke and drop the caret.
    this.listen(input, "input", () => {
      this.state.query = input.value;
      // A pasted "a, b, c" is three tags, not one with commas in it.
      if (this.state.query.includes(",")) {
        const parts = splitTags(this.state.query);
        this.state.query = "";
        this.#commit(parts);
        return;
      }
      this.highlight = -1;
      this.#openList();
    });

    this.listen(input, "focus", () => this.#openList());
    this.listen(input, "keydown", (e) => this.#onKeydown(e));

    this.listen(list, "mousedown", (e) => {
      // mousedown, not click: the input's blur would close the list first.
      const option = e.target.closest(".tag-search__option");
      if (!option) return;
      e.preventDefault();
      this.#commit([option.getAttribute("data-tag") || option.getAttribute("data-create")]);
    });

    this.$$("[data-remove]").forEach((chip) =>
      this.listen(chip, "click", () => this.#remove(chip.getAttribute("data-remove")))
    );

    this.listen(document, "mousedown", (e) => {
      if (this.state.open && !this.el.contains(e.target)) this.#closeList();
    });
  }

  #onKeydown(e) {
    const count = this.#optionCount();
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!count) return;
      this.highlight = e.key === "ArrowDown" ? (this.highlight + 1) % count : (this.highlight <= 0 ? count : this.highlight) - 1;
      this.#paintList();
      this.$(".tag-search__option--active")?.scrollIntoView?.({ block: "nearest" });
      return;
    }
    if (e.key === "Escape") {
      this.#closeList();
      return;
    }
    if (e.key === "Enter") {
      // The form's own Enter handler already blocks submission; this decides
      // what Enter *does* instead — commit the cursor, else the top hit, else
      // whatever was typed (which canonicalTag may still snap onto a known tag).
      e.preventDefault();
      // Nothing typed and no cursor: Enter would otherwise add whatever tag
      // happens to top the list, which is not what an empty field promises.
      if (this.highlight < 0 && !this.state.query.trim()) return;
      const picked = this.#optionAt(this.highlight >= 0 ? this.highlight : 0) || this.state.query;
      this.#commit([picked]);
      return;
    }
    if (e.key === "Backspace" && !this.state.query && this.state.tags.length) {
      this.#remove(this.state.tags[this.state.tags.length - 1], { keepTyping: true });
    }
  }

  /** Add tags, snapping each onto its existing spelling where there is one. */
  #commit(candidates) {
    const next = [...this.state.tags];
    for (const candidate of candidates) {
      const tag = canonicalTag(candidate, this.props.vocabulary || []);
      if (!tag) continue;
      if (next.some((existing) => foldTag(existing) === foldTag(tag))) continue;
      next.push(tag);
    }
    this.state.query = "";
    this.highlight = -1;
    // Adding a tag means more are probably coming: keep the caret in the field
    // and the list up.
    this.#update(next, { focus: true, open: true });
  }

  /**
   * Remove a chip. `keepTyping` separates the two ways that happens: Backspace
   * comes from inside the field mid-entry, so focus and the open list stay put,
   * while *clicking* a chip is a tidy-up — it must not pull the caret into the
   * field, because focusing it pops the suggestion list open over the form.
   */
  #remove(tag, { keepTyping = false } = {}) {
    this.#update(this.state.tags.filter((t) => t !== tag), {
      focus: keepTyping,
      open: keepTyping && this.state.open,
    });
  }

  #update(tags, { focus, open }) {
    this.refocus = focus;
    this.props.onChange?.(tags);
    this.setState({ tags, query: this.state.query, open });
  }

  #openList() {
    if (this.state.open) {
      this.#paintList();
      return;
    }
    this.state.open = true;
    const list = this.$(".tag-search__suggestions");
    list.hidden = false;
    this.$(".tag-search__input").setAttribute("aria-expanded", "true");
    this.#paintList();
  }

  /** Repaint the options in place — see the `input` handler for why not setState. */
  #paintList() {
    const list = this.$(".tag-search__suggestions");
    if (list) list.innerHTML = this.#optionMarkup();
  }

  #closeList() {
    this.state.open = false;
    this.highlight = -1;
    const list = this.$(".tag-search__suggestions");
    if (list) list.hidden = true;
    this.$(".tag-search__input")?.setAttribute("aria-expanded", "false");
  }
}
